import { useMemo } from 'react'
import { useAccount } from 'wagmi'
import { T } from '@/lib/theme'
import { Button, Panel, SectionLabel } from '@/components/ui'
import { ADDRESSES } from '@/lib/contracts'
import bribeAbi from '@/lib/abis/BribeMarket.json'
import { fmtBalance, toNumber } from '@/lib/utils'
import { gaugeLabel } from '@/lib/gaugeLabel'
import { useAction } from '@/hooks/useAction'
import { useGaugeVote } from '@/hooks/useGaugeVote'
import { useBribes } from '@/hooks/useBribes'
import { useVaultsList } from '@/hooks/useVaultsList'

/// Bribe claims (BribeMarket): incentives anyone deposited on a gauge, paid to the veNFT holders who
/// voted for it that epoch. This is the claim side; posting a bribe is a deferred surface. One row
/// per gauge with anything owed; Claim banks every (epoch, token) for that gauge in one call.
export function BribePanel({ d, onTx }) {
  const { address: user } = useAccount()
  const action = useAction()
  const bm = ADDRESSES.BRIBE_MARKET
  const { farms } = useGaugeVote(user)
  const { vaults } = useVaultsList()
  const { groups, meta, hasAny, notDeployed, refetch } = useBribes(user)

  const farmVault = useMemo(() => {
    const m = {}
    for (const f of farms) m[f.id] = f.vault
    return m
  }, [farms])

  if (notDeployed) return null

  async function claim(group) {
    const epochs = group.claims.map((c) => BigInt(c.epoch))
    const tokens = group.claims.map((c) => c.token)
    const res = await action.run({
      calls: [{ address: bm, abi: bribeAbi, functionName: 'claim', args: [group.farmId, epochs, tokens] }],
      successMsg: 'Bribes claimed.',
    })
    if (res) { await refetch(); onTx?.() }
  }

  return (
    <Panel style={{ padding: 32 }}>
      <SectionLabel>Bribes</SectionLabel>
      <p style={{ color: T.textDim, fontSize: 14, lineHeight: 1.6, margin: '10px 0 18px' }}>
        Extra incentives placed on the gauges you voted for. Whoever wants a gauge to win emissions can
        pay for votes, and that payment goes to the holders who voted for it. Claim yours here.
      </p>

      {!hasAny && (
        <div style={{ color: T.textDim, fontSize: 14 }}>
          {user ? 'No bribes to claim. Vote for a gauge that has incentives to earn them.' : 'Connect your wallet to see claimable bribes.'}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {groups.map((g) => (
          <div key={g.farmId.toString()} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <div>
              <div style={{ color: T.text, fontSize: 15 }}>{gaugeLabel(farmVault[Number(g.farmId)], d, vaults)}</div>
              <div style={{ color: T.positive, fontSize: 13 }}>
                {Object.entries(g.byToken).map(([tk, amt], i) => (
                  <span key={tk}>
                    {i > 0 && <span style={{ color: T.textDim }}> + </span>}
                    {fmtBalance(toNumber(amt, meta[tk]?.decimals ?? 18), 4)} {meta[tk]?.symbol ?? '…'}
                  </span>
                ))}
              </div>
            </div>
            <Button onClick={() => claim(g)} disabled={action.pending}>Claim</Button>
          </div>
        ))}
      </div>
    </Panel>
  )
}
